// developer.effects.ts
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, mergeMap, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ToastService } from '../../toasts/services/toast.service';
import { DeveloperActions } from '../actions/developer.actions';
import { DeveloperService } from '../service/developer.service';
import { ApiResponse, UserListItem } from '../../../shared/models';
import { CreateAndAddToSubmissionResponse } from '../../../shared/models/developer-mode/create-and-add-submission-response.model';

@Injectable()
export class DeveloperEffects {
  constructor(
    private actions$: Actions,
    private developerService: DeveloperService,
    private toastService: ToastService
  ) {}

  createAndAddSubmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeveloperActions.createAndAddSubmission),
      mergeMap((action) =>
        this.developerService.createAndAddToSubmission(action.payload).pipe(
          map((response: ApiResponse<CreateAndAddToSubmissionResponse>) =>
            DeveloperActions.createAndAddSubmissionSuccess({ response })
          ),
          catchError((error) => of(DeveloperActions.createAndAddSubmissionFail({ error })))
        )
      )
    )
  );

  createWeightEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeveloperActions.createWeightEntry),
      mergeMap((action) =>
        this.developerService.createWeightEntry(action.payload).pipe(
          map(() => DeveloperActions.createWeightEntrySuccess()),
          catchError((error) => of(DeveloperActions.createWeightEntryFail({ error })))
        )
      )
    )
  );

  triggerUserMail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeveloperActions.triggerUserMail),
      mergeMap((action) =>
        this.developerService
          .triggerUserMail(action.userId, action.mailType, action.requestedByUserId)
          .pipe(
            map(() => DeveloperActions.triggerUserMailSuccess()),
            catchError((error) => of(DeveloperActions.triggerUserMailFail({ error })))
          )
      )
    )
  );

  showCreateAndAddSubmissionSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DeveloperActions.createAndAddSubmissionSuccess),
        tap(() => {
          this.toastService.showSuccess(
            'Erfolg!',
            'Die Umfrage wurde erfolgreich erstellt und hinzugefügt.'
          );
        })
      ),
    { dispatch: false }
  );

  showCreateAndAddSubmissionFailToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DeveloperActions.createWeightEntryFail),
        tap(() => {
          this.toastService.showError('Fehler!', 'Es konnten keine befragungen erstellt werden.');
        })
      ),
    { dispatch: false }
  );

  showWeightSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DeveloperActions.createWeightEntrySuccess),
        tap(() => {
          this.toastService.showSuccess('Erfolg!', 'Das Gewicht wurde erfolgreich gespeichert.');
        })
      ),
    { dispatch: false }
  );

  showWeightFailToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DeveloperActions.createWeightEntryFail),
        tap(() => {
          this.toastService.showError('Fehler!', 'Das Gewicht konnte nicht gespeichert werden.');
        })
      ),
    { dispatch: false }
  );

  showTriggerMailSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DeveloperActions.triggerUserMailSuccess),
        tap(() => {
          this.toastService.showSuccess('Erfolg!', 'Die E-Mail wurde erfolgreich versendet.');
        })
      ),
    { dispatch: false }
  );

  showTriggerMailFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DeveloperActions.triggerUserMailFail),
        tap(() => {
          this.toastService.showError('Fehler!', 'Die E-Mail konnte nicht versendet werden.');
        })
      ),
    { dispatch: false }
  );

  connectUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeveloperActions.connectUsers),
      mergeMap((action) =>
        this.developerService
          .connectUsers(action.connectionType, action.sourceUserId, action.targetEmail)
          .pipe(
            map((response: ApiResponse<UserListItem>) =>
              DeveloperActions.connectUsersSuccess({
                user: response.data,
                connectionType: action.connectionType,
              })
            ),
            catchError((error) => of(DeveloperActions.connectUsersFail({ error })))
          )
      )
    )
  );

  showConnectUsersSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DeveloperActions.connectUsersSuccess),
        tap(() => {
          this.toastService.showSuccess('Erfolg!', 'Verbindung erfolgreich erstellt.');
        })
      ),
    { dispatch: false }
  );

  showConnectUsersFailToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DeveloperActions.connectUsersFail),
        tap(() => {
          this.toastService.showError('Fehler!', 'Die Verbindung konnte nicht erstellt werden.');
        })
      ),
    { dispatch: false }
  );

  removeUserConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeveloperActions.removeUserConnection),
      mergeMap((action) =>
        this.developerService
          .removeUserConnection(action.connectionType, action.sourceUserId, action.targetUserId)
          .pipe(
            map(() =>
              DeveloperActions.removeUserConnectionSuccess({
                removedUserId: action.targetUserId,
                connectionType: action.connectionType,
              })
            ),
            catchError((error) => of(DeveloperActions.removeUserConnectionFail({ error })))
          )
      )
    )
  );

  showRemoveConnectionSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DeveloperActions.removeUserConnectionSuccess),
        tap(() => {
          this.toastService.showSuccess('Erfolg!', 'Verbindung wurde entfernt.');
        })
      ),
    { dispatch: false }
  );

  showRemoveConnectionFailToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DeveloperActions.removeUserConnectionFail),
        tap(() => {
          this.toastService.showError('Fehler!', 'Verbindung konnte nicht entfernt werden.');
        })
      ),
    { dispatch: false }
  );

}
